/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
    FaSearch, FaAngleLeft, FaAngleRight, FaPlus, FaTimes, FaEye, FaTrash, FaCheckCircle, FaEdit, FaTag, FaDollarSign, FaBoxes, FaImage, FaSpinner
} from 'react-icons/fa';
import Select from 'react-select';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { UserContext } from '../../App';

const API_URL = process.env.REACT_APP_API_URL;

// Custom Hook to fetch suppliers (used only for admin)
const useFetchSuppliers = (userType) => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (userType === 'admin') {
            const accessToken = localStorage.getItem("accessToken");
            const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            };
            const fetchSuppliers = async () => {
                setLoading(true);
                try {
                    const url = `${API_URL}/suppliers`;
                    const res = await fetch(url, { headers });
                    if (!res.ok) {
                        throw new Error("Échec de la récupération des fournisseurs.");
                    }
                    const data = await res.json();
                    setSuppliers(data.suppliers);
                } catch (err) {
                    console.error("Erreur API:", err);
                } finally {
                    setLoading(false);
                }
            };
            fetchSuppliers();
        }
    }, [userType]);

    return { suppliers, loading };
};

// Custom Hook to fetch products (adapted for both admin and supplier)
const useFetchProducts = (user, searchTerm, page, refreshKey) => {
    const [products, setProducts] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const productsPerPage = 10;

    useEffect(() => {
        const accessToken = localStorage.getItem("accessToken");
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        };
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                let url;
                if (user) {
                    const queryParams = new URLSearchParams({
                        q: searchTerm,
                        page,
                        limit: productsPerPage,
                    }).toString();
                    url = `${API_URL}/suppliers/${user.userId}/products?${queryParams}`;
                } else {
                    throw new Error("User not authenticated.");
                }
                const res = await fetch(url, { headers });
                if (!res.ok) {
                    throw new Error("Échec de la récupération des produits.");
                }

                const data = await res.json();
                const fetchedProducts = data?.products || [];
                const fetchedTotalCount = data?.totalCount || fetchedProducts.length;

                const formattedProducts = fetchedProducts.map(p => ({
                    ...p,
                    price: Number(p.price)
                }));

                setProducts(formattedProducts);
                setTotalCount(fetchedTotalCount);

            } catch (err) {
                setError("Échec de la récupération des produits. Veuillez réessayer plus tard.");
                setProducts([]);
                setTotalCount(0);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user, searchTerm, page, refreshKey]);

    return { products, totalCount, loading, error, productsPerPage };
};

// Hook personnalisé pour récupérer les catégories de produits
const useFetchCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const accessToken = localStorage.getItem("accessToken");
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        };
        const fetchCategories = async () => {
            setLoading(true);
            try {
                const url = `${API_URL}/productCategories`;
                const res = await fetch(url, { headers });
                if (!res.ok) {
                    throw new Error("Échec de la récupération des catégories.");
                }
                const data = await res.json();
                setCategories(data);
            } catch (err) {
                console.error("Erreur API:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchCategories();
    }, []);
    return { categories, loading };
};

// Composant de modal pour la confirmation de suppression
const DeleteProductModal = ({ isOpen, onClose, product, onConfirmDelete }) => {
    if (!isOpen || !product) return null;
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-8 text-center">
                <FaTrash className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Êtes-vous sûr ?</h2>
                <p className="text-gray-600 mb-6">
                    Cette action est irréversible. Le produit <b>{product.productName}</b> sera supprimé de manière permanente.
                </p>
                <div className="flex justify-center space-x-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onConfirmDelete(product.productId)}
                        className="px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
                    >
                        Confirmer la suppression
                    </button>
                </div>
            </div>
        </div>
    );
};

// Composant de modal pour ajouter ou modifier un produit (adapted for suppliers)
const ProductFormModal = ({ isOpen, onClose, productToEdit, onSuccess }) => {
    const { user } = useContext(UserContext); // Get user from context
    const isEditing = !!productToEdit;
    const { categories, loading: categoriesLoading } = useFetchCategories();
    const { suppliers, loading: suppliersLoading } = useFetchSuppliers(user?.userType);
    const [formData, setFormData] = useState({
        productName: '',
        description: '',
        price: '',
        unit: '',
        stockQuantity: '',
        categoryId: '',
        supplierId: '',
    });
    const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (isEditing && productToEdit) {
            setFormData({
                productName: productToEdit.productName || '',
                description: productToEdit.description || '',
                price: productToEdit.price || '',
                stockQuantity: productToEdit.stockQuantity || '',
                unit: productToEdit.unit || '',
                categoryId: productToEdit.categoryId || '',
                supplierId: productToEdit.supplierId || '',
            });
            const imageUrls = productToEdit.images
                ? productToEdit.images.split(',').map(url => url.trim())
                : [];
            setUploadedImageUrls(imageUrls);
        } else {
            setFormData({
                productName: '',
                description: '',
                price: '',
                unit: '',
                stockQuantity: '',
                categoryId: '',
                supplierId: user?.userType === 'supplier' ? user.userId : '',
            });
            setUploadedImageUrls([]);
        }
    }, [productToEdit, isEditing, user]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        setError(null);
        const formData = new FormData();
        formData.append('image', file);
        try {
            const res = await fetch(`${API_URL}/upload-image`, {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || "Failed to upload image.");
            }
            setUploadedImageUrls(prevUrls => [...prevUrls, data.imageUrl]);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveImage = (urlToRemove) => {
        setUploadedImageUrls(prevUrls => prevUrls.filter(url => url !== urlToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);
        const payload = {
            productName: formData.productName,
            description: formData.description,
            price: parseFloat(formData.price),
            unit: formData.unit,
            stockQuantity: parseInt(formData.stockQuantity, 10),
            categoryId: formData.categoryId,
            supplierId: user?.userType === 'supplier' ? user.userId : formData.supplierId,
            images: uploadedImageUrls
        };
        try {
            const accessToken = localStorage.getItem("accessToken");
            const method = isEditing ? 'PUT' : 'POST';
            const endpoint = isEditing ? `${API_URL}/products/${productToEdit.productId}` : `${API_URL}/products`;
            const res = await fetch(endpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (!res.ok) {
                const errorMessage = data.message || `Échec de ${isEditing ? 'la modification' : "l'ajout"} du produit.`;
                throw new Error(errorMessage);
            }
            setSuccess(`Produit ${isEditing ? 'modifié' : 'ajouté'} avec succès!`);
            onSuccess(data);
            setTimeout(() => onClose(), 1500);
        } catch (err) {
            setError(err.message || "Une erreur est survenue lors de l'opération.");
        } finally {
            setLoading(false);
        }
    };
    const options = suppliers.map(supplier => ({
        value: supplier.userId,
        label: `${supplier.firstName} ${supplier.lastName}`,
    }));
    const categoryOptions = categories.map(category => ({
        value: category.categoryId,
        label: category.categoryName,
    }));
    if (!isOpen) return null;
    if (categoriesLoading || (user?.userType === 'admin' && suppliersLoading)) {
        return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
                <div className="text-white text-xl">Chargement...</div>
            </div>
        );
    }
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">{isEditing ? 'Modifier un produit' : 'Ajouter un nouveau produit'}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <FaTimes className="w-6 h-6" />
                    </button>
                </div>
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm" role="alert">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2" role="alert">
                        <FaCheckCircle className="text-green-500" />
                        <span>{success}</span>
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4">
                        <input
                            type="text"
                            name="productName"
                            value={formData.productName}
                            onChange={handleFormChange}
                            placeholder="Nom du produit"
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        {user?.userType === 'admin' && (
                            <div className="relative">
                                <Select
                                    options={options}
                                    value={options.find(option => option.value === formData.supplierId)}
                                    onChange={(selectedOption) => {
                                        setFormData({ ...formData, supplierId: selectedOption.value });
                                    }}
                                    isSearchable={true}
                                    placeholder="Sélectionner un fournisseur"
                                />
                            </div>
                        )}
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleFormChange}
                            placeholder="Description"
                            rows="4"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <div className="relative">
                                <FaDollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleFormChange}
                                    placeholder="Prix (CDF)"
                                    required
                                    min="0"
                                    step="0.01"
                                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                            <div className="relative">
                                <FaBoxes className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="number"
                                    name="stockQuantity"
                                    value={formData.stockQuantity}
                                    onChange={handleFormChange}
                                    placeholder="Quantité en stock"
                                    required
                                    min="0"
                                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                        </div>
                            <div className="relative">
                                <FaBoxes className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <select name='unit' value={formData.unit} onChange={handleFormChange} required className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400">
                                    <option value="" disabled>Sélectionner une unité</option>
                                    <option value="kg">Kilogramme (kg)</option>
                                    <option value="g">Gramme (g)</option>
                                    <option value="l">Litre (l)</option>
                                    <option value="ml">Millilitre (ml)</option>
                                    <option value="pcs">Pièces (pcs)</option>
                                    <option value="box">Boîte (box)</option>
                                    <option value="pack">Pack (pack)</option>
                                </select>
                            </div>
                        <div className="relative">
                            <FaTag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <Select
                                options={categoryOptions}
                                value={categoryOptions.find(option => option.value === formData.categoryId)}
                                onChange={(selectedOption) => {
                                    setFormData({ ...formData, categoryId: selectedOption.value });
                                }}
                                isSearchable={true}
                                placeholder="Sélectionner une catégorie"
                                className="w-full"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            <input
                                type="file"
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                onChange={handleImageUpload}
                                disabled={isUploading}
                            />
                            {isUploading ? (
                                <div className="text-gray-500 flex items-center justify-center">
                                    <FaSpinner className="animate-spin mr-2" /> Téléchargement...
                                </div>
                            ) : (
                                <div className="text-gray-500">
                                    <FaImage className="mx-auto text-4xl mb-2" />
                                    <p>Cliquez ou glissez une image pour la télécharger</p>
                                </div>
                            )}
                        </div>
                        {uploadedImageUrls.length > 0 && (
                            <div className="mt-4 grid grid-cols-4 gap-4">
                                {uploadedImageUrls.map(url => (
                                    <div key={url} className="relative aspect-w-1 aspect-h-1 rounded-lg overflow-hidden">
                                        <img src={url} alt="Uploaded" className="object-cover w-full h-full" />
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveImage(url)}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                                        >
                                            <FaTimes className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading || isUploading}
                            className="px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition-colors disabled:bg-indigo-300"
                        >
                            {loading ? `Enregistrement...` : isEditing ? 'Sauvegarder les modifications' : 'Ajouter le produit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Composant de modal pour les détails du produit
const ProductDetailsModal = ({ isOpen, onClose, product, categories }) => {
    if (!isOpen || !product) return null;
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-8 my-8">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Détails du produit</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <FaTimes className="w-6 h-6" />
                    </button>
                </div>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-50 p-6 rounded-xl shadow-inner">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Informations</h3>
                            <p><strong>Nom:</strong> {product.productName}</p>
                            <p><strong>Catégorie:</strong> <span className="bg-purple-100 text-purple-800 font-semibold py-1 px-3 rounded-full text-xs">{product.categoryName || 'Non catégorisé'}</span></p>
                            <p><strong>Fournisseur:</strong> {product.supplierFirstName || 'N/A'} {product.supplierLastName || ''}</p>
                            <p><strong>Prix:</strong> <span className="font-semibold">CDF {parseFloat(product.price).toFixed(2)}</span></p>
                            <p><strong>En stock:</strong> <span className="font-semibold">{product.stockQuantity}</span></p>
                            <p><strong>Ajouté le:</strong> {new Date(product.createdAt).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <div className="bg-gray-50 p-6 rounded-xl shadow-inner">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Description</h3>
                            <p className="text-gray-700">{product.description || 'Aucune description fournie.'}</p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Images</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {product.images && product.images.length > 0 ? (
                                // FIX: Split the comma-separated string into an array
                                product.images.split(',').map((imgUrl, index) => (
                                    <div key={index} className="aspect-w-1 aspect-h-1 overflow-hidden rounded-lg">
                                        <img
                                            src={imgUrl.trim()} // Use trim() to remove any whitespace
                                            alt={`${product.productName} - View ${index + 1}`}
                                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                                            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/400x400/E5E7EB/4B5563?text=Image+non+disponible" }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 col-span-full text-center">Aucune image disponible.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main ProductsPage component (adapted for both admin and supplier)
const ProductsPage = () => {
    const { user } = useContext(UserContext);
    const [inputTerm, setInputTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusMessage, setStatusMessage] = useState(null);
    const [messageType, setMessageType] = useState('success');

    const { products, totalCount, loading, error, productsPerPage } = useFetchProducts(user, searchTerm, page, refreshKey);
    const { categories } = useFetchCategories();
    const isAdmin = user?.userType === 'admin';

    // Analytics data (only for admin)
    const getCategoryChartData = useCallback(() => {
        const categoryCounts = {};
        products.forEach(product => {
            const categoryName = product.categoryName || 'Uncategorized';
            categoryCounts[categoryName] = (categoryCounts[categoryName] || 0) + 1;
        });
        return Object.keys(categoryCounts).map(name => ({
            name: name,
            products: categoryCounts[name],
        }));
    }, [products]);
    const chartData = getCategoryChartData();

    const getPriceChartData = useCallback(() => {
        const priceRanges = {
            '0-20 CDF': 0,
            '21-50 CDF': 0,
            '51-100 CDF': 0,
            '101+ CDF': 0,
        };
        products.forEach(product => {
            if (product.price <= 20) {
                priceRanges['0-20 CDF']++;
            } else if (product.price > 20 && product.price <= 50) {
                priceRanges['21-50 CDF']++;
            } else if (product.price > 50 && product.price <= 100) {
                priceRanges['51-100 CDF']++;
            } else {
                priceRanges['101+ CDF']++;
            }
        });
        const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300'];
        return Object.keys(priceRanges).map((range, index) => ({
            name: range,
            value: priceRanges[range],
            color: colors[index],
        }));
    }, [products]);
    const priceData = getPriceChartData();

    const getCreationChartData = useCallback(() => {
        const creationDates = {};
        products.forEach(product => {
            const date = new Date(product.createdAt);
            const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
            creationDates[monthYear] = (creationDates[monthYear] || 0) + 1;
        });
        return Object.keys(creationDates)
            .sort((a, b) => {
                const [aMonth, aYear] = a.split('/').map(Number);
                const [bMonth, bYear] = b.split('/').map(Number);
                if (aYear !== bYear) return aYear - bYear;
                return aMonth - bMonth;
            })
            .map(monthYear => ({
                name: monthYear,
                products: creationDates[monthYear],
            }));
    }, [products]);
    const creationData = getCreationChartData();

    const totalPages = Math.ceil(totalCount / productsPerPage);

    const showMessage = (message, type = 'success') => {
        setStatusMessage(message);
        setMessageType(type);
        setTimeout(() => setStatusMessage(null), 5000);
    };

    const handleInputChange = (e) => setInputTerm(e.target.value);
    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(inputTerm);
        setPage(1);
    };
    const handleSuccess = () => {
        setRefreshKey(prev => prev + 1);
        showMessage(`Opération réussie! La liste des produits a été mise à jour.`);
    };
    const handleViewProduct = (product) => {
        setSelectedProduct(product);
        setIsViewModalOpen(true);
    };
    const handleEditProduct = (product) => {
        setSelectedProduct(product);
        setIsEditModalOpen(true);
    };
    const handleDeleteProduct = (product) => {
        setSelectedProduct(product);
        setIsDeleteModalOpen(true);
    };
    const handleConfirmDelete = async (productId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const res = await fetch(`${API_URL}/products/${productId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Échec de la suppression du produit.");
            }
            setIsDeleteModalOpen(false);
            setRefreshKey(prev => prev + 1);
            showMessage("Produit supprimé avec succès !");
        } catch (err) {
            console.error("Erreur lors de la suppression:", err);
            showMessage(err.message || "Une erreur est survenue lors de la suppression.", 'error');
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900">
                            Gestion des produits 📦
                        </h1>
                        <p className="mt-2 text-lg text-gray-500">
                            Visualisez et gérez vos produits et leurs images.
                        </p>
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center space-x-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors"
                    >
                        <FaPlus />
                        <span>Ajouter un produit</span>
                    </button>
                </header>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-white p-6 rounded-xl shadow-lg flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-600">Total Products</h3>
                            <p className="text-4xl font-bold text-gray-900">{totalCount}</p>
                        </div>
                        <FaBoxes className="text-indigo-400 text-4xl" />
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-lg flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-600">Categories</h3>
                            <p className="text-4xl font-bold text-gray-900">{categories.length}</p>
                        </div>
                        <FaTag className="text-purple-400 text-4xl" />
                    </div>
                </div>
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Analyse des produits</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            <div className="bg-white px-6 py-12 rounded-xl shadow-lg h-96">
                                <h4 className="text-lg font-semibold text-gray-700 text-center mb-2">Produits par catégorie</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="products" fill="#8884d8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="bg-white px-6 py-12 rounded-xl shadow-lg h-96">
                                <h4 className="text-lg font-semibold text-gray-700 text-center mb-2">Distribution des prix</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={priceData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={80}
                                            fill="#8884d8"
                                            label
                                        >
                                            {priceData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="bg-white px-6 py-12 rounded-xl shadow-lg h-96">
                                <h4 className="text-lg font-semibold text-gray-700 text-center mb-2">Tendance de création des produits</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={creationData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line type="monotone" dataKey="products" stroke="#ff7300" activeDot={{ r: 8 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
               
                {statusMessage && (
                    <div className={`border px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2 ${messageType === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'}`} role="alert">
                        <FaCheckCircle className="text-green-500" />
                        <span>{statusMessage}</span>
                    </div>
                )}
                <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg mb-6">
               
                        <form onSubmit={handleSearch}>
                            <div className="flex items-center space-x-4 mb-6">
                                <div className="relative flex-1">
                                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={inputTerm}
                                        onChange={handleInputChange}
                                        placeholder="Rechercher par nom de produit..."
                                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="px-8 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors"
                                >
                                    Rechercher
                                </button>
                            </div>
                        </form>
                   
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="text-2xl text-gray-700 animate-pulse">Chargement des produits...</div>
                        </div>
                    ) : error ? (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl text-sm" role="alert">
                            {error}
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full table-auto">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm font-semibold">
                                            <th className="py-4 px-6 text-left rounded-tl-xl">Nom du produit</th>
                                            <th className="py-4 px-6 text-left">Catégorie</th>
                                            <th className="py-4 px-6 text-left">Prix (CDF)</th>
                                            <th className="py-4 px-6 text-left">Stock</th>
                                            {isAdmin && <th className="py-4 px-6 text-left">Fournisseur</th>}
                                            <th className="py-4 px-6 text-left">Ajouté le</th>
                                            <th className="py-4 px-6 text-center rounded-tr-xl">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                        {products.length > 0 ? (
                                            products.map((product) => {
                                                return (
                                                    <tr key={product.productId} className="hover:bg-gray-50 transition-colors duration-200">
                                                        <td className="py-4 px-6 text-left whitespace-nowrap">
                                                            <div className="flex items-center space-x-3">
                                                                {product.images && product.images.length > 0 ? (
                                                                    // FIX: Split the string and get the first URL
                                                                    <img
                                                                        src={product.images.split(',')[0].trim()}
                                                                        alt={product.productName}
                                                                        className="w-10 h-10 object-cover rounded-md"
                                                                        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/40x40/E5E7EB/4B5563?text=N/A" }}
                                                                    />
                                                                ) : (
                                                                    <FaImage className="w-10 h-10 text-gray-300 p-2 border rounded-md" />
                                                                )}
                                                                <span className="font-medium text-gray-800">
                                                                    {product.productName || 'N/A'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-6 text-left">
                                                            <span className="bg-purple-100 text-purple-800 font-semibold py-1 px-3 rounded-full text-xs">
                                                                {product.categoryName || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-6 text-left">
                                                            <span className="font-medium text-gray-800">
                                                                {parseFloat(product.price).toFixed(2) || 'N/A'} / {product.unit}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-6 text-left">
                                                            <span className="text-gray-500">
                                                                {product.stockQuantity !== undefined ? product.stockQuantity : 'N/A'}
                                                            </span>
                                                        </td>
                                                        {isAdmin && (
                                                            <td className="py-4 px-6 text-left">
                                                                <span className="text-gray-500">
                                                                    {product.supplierFirstName || 'N/A'} {product.supplierLastName || ''}
                                                                </span>
                                                            </td>
                                                        )}
                                                        <td className="py-4 px-6 text-left">
                                                            <span className="text-gray-500 text-sm">
                                                                {product.createdAt ? new Date(product.createdAt).toLocaleDateString('fr-FR') : 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-6 text-center">
                                                            <div className="flex justify-center space-x-2">
                                                                <button
                                                                    onClick={() => handleViewProduct(product)}
                                                                    className="p-2 rounded-full text-indigo-500 hover:bg-indigo-100 transition-colors"
                                                                    title="Voir les détails"
                                                                >
                                                                    <FaEye className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleEditProduct(product)}
                                                                    className="p-2 rounded-full text-blue-500 hover:bg-blue-100 transition-colors"
                                                                    title="Modifier le produit"
                                                                >
                                                                    <FaEdit className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteProduct(product)}
                                                                    className="p-2 rounded-full text-red-500 hover:bg-red-100 transition-colors"
                                                                    title="Supprimer le produit"
                                                                >
                                                                    <FaTrash className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={isAdmin ? "7" : "6"} className="py-10 text-center text-gray-500">
                                                    Aucun produit trouvé.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                         
                                <div className="flex justify-between items-center mt-6">
                                    <div className="text-sm text-gray-600">
                                        Affichage de {(page - 1) * productsPerPage + 1} à {Math.min(page * productsPerPage, totalCount)} sur {totalCount} produits
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setPage(page - 1)}
                                            disabled={page === 1}
                                            className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FaAngleLeft className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setPage(page + 1)}
                                            disabled={page >= totalPages}
                                            className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FaAngleRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                      
                        </>
                    )}
                </div>
            </div>
            <ProductFormModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={handleSuccess}
                productToEdit={null}
            />
            <ProductFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSuccess={handleSuccess}
                productToEdit={selectedProduct}
            />
            <ProductDetailsModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                product={selectedProduct}
                categories={categories}
            />
            <DeleteProductModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                product={selectedProduct}
                onConfirmDelete={handleConfirmDelete}
            />
        </div>
    );
};
export default ProductsPage;